import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import Button from "./Button";

// A promise-based replacement for window.confirm — call `await confirm({...})`
// and branch on the boolean it resolves to. Rendered as a Tailwind dialog by
// the single <ConfirmProvider> mounted at the app root.
export type ConfirmOptions = {
  title?: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  // "danger" → red confirm button (destructive actions); "brand" → gradient.
  tone?: "danger" | "brand";
};

type ConfirmFn = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within <ConfirmProvider>");
  }
  return ctx;
};

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options = {}) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  }, []);

  // Esc cancels, Enter confirms while the dialog is open.
  useEffect(() => {
    if (!opts) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settle(false);
      else if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opts, settle]);

  const tone = opts?.tone ?? "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 backdrop-blur-sm p-4 anim-in"
            onClick={() => settle(false)}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-5">
                <div className="flex items-start gap-3">
                  <span
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      tone === "danger"
                        ? "bg-rose-50 text-rose-600"
                        : "bg-brand-50 text-brand-600"
                    }`}
                  >
                    <AlertTriangle size={18} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-display text-base font-semibold text-ink-900 tracking-tight">
                      {opts.title || "Are you sure?"}
                    </h2>
                    {opts.message && (
                      <p className="text-sm text-ink-500 mt-1 leading-relaxed">
                        {opts.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-6 py-4 bg-ink-50/60 border-t border-ink-100">
                <Button variant="secondary" onClick={() => settle(false)}>
                  {opts.cancelLabel || "Cancel"}
                </Button>
                <Button
                  variant={tone === "danger" ? "danger" : "gradient"}
                  onClick={() => settle(true)}
                  autoFocus
                >
                  {opts.confirmLabel || "Confirm"}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
